import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { resolveAgency } from '../auth';
import Request from './Request';

/**
 * The pool status when `response` is the gateway's 429 TOKEN_LIMIT_REACHED,
 * else null. Reads the body, so call it once per response.
 */
export const readTokenLimitReached = async response => {
  if (response.status !== 429) {
    return null;
  }

  try {
    const body = JSON.parse(await response.text());

    if (body.error !== 'TOKEN_LIMIT_REACHED' || !body.tokenStatus) {
      return null;
    }

    return _objectSpread(_objectSpread({}, body.tokenStatus), {}, {
      limitReached: true
    });
  } catch {
    return null;
  }
};
/**
 * Seconds until `status` resets, from resetsAt (so it stays right while the
 * page is open), else the gateway's secondsUntilReset.
 */

export const secondsUntilTokenReset = (status, now = Date.now()) => {
  const resetsAt = Date.parse(status.resetsAt);

  if (Number.isNaN(resetsAt)) {
    return Math.max(0, status.secondsUntilReset);
  }

  return Math.max(0, Math.ceil((resetsAt - now) / 1000));
};
/** "2 h 15 min", "45 min", "1 min": minutes rounded up so it never says 0. */

export const formatTokenWait = seconds => {
  const minutes = Math.max(1, Math.ceil(seconds / 60));
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;

  if (hours === 0) {
    return `${rest} min`;
  }

  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
};
export class AgentGateway extends Request {
  constructor({
    url,
    keycloak,
    agency,
    signal
  }) {
    super({
      url,
      signal,
      keycloak
    });

    _defineProperty(this, "keycloak", void 0);

    _defineProperty(this, "agency", void 0);

    _defineProperty(this, "commonHeaders", {
      Authorization: '',
      'Content-Type': 'application/json',
      Accept: 'application/json'
    });

    _defineProperty(this, "agencyQs", () => this.agency ? {
      agency: this.agency
    } : {});

    _defineProperty(this, "postAuditPatient", async (patientNumber, correlationId) => {
      const response = await this.post({
        api: '/api/agents/chart-audit/audit-patient',
        qs: this.agencyQs(),
        data: {
          patientNumber
        },
        headers: {
          'X-Correlation-ID': correlationId
        }
      });
      return response;
    });

    _defineProperty(this, "postAuditPatientWithConfig", async (patientNumber, dashboardPrintConfig, correlationId) => {
      const response = await this.post({
        api: '/api/agents/chart-audit/audit-patient-with-config',
        qs: this.agencyQs(),
        data: {
          patientNumber,
          dashboardPrintConfig
        },
        headers: {
          'X-Correlation-ID': correlationId
        }
      });
      return response;
    });

    _defineProperty(this, "listRequests", async (params = {}) => {
      var _params$size, _params$page;

      const qs = _objectSpread({
        agentId: params.agentId || 'chart-audit',
        sort: 'createdAt,DESC',
        size: String((_params$size = params.size) !== null && _params$size !== void 0 ? _params$size : 50),
        page: String((_params$page = params.page) !== null && _params$page !== void 0 ? _params$page : 0)
      }, this.agencyQs());

      if (params.patientNumber) {
        qs.patientNumber = params.patientNumber;
      }

      if (params.status) {
        qs.status = params.status;
      }

      const response = await this.get({
        api: '/api/agent-requests',
        qs
      });
      return response;
    });

    _defineProperty(this, "getRequest", async key => {
      const response = await this.get({
        api: `/api/agent-requests/${key}`,
        qs: this.agencyQs()
      });
      return response;
    });

    _defineProperty(this, "getTokenStatus", async () => {
      const response = await this.get({
        api: '/api/agents/token-status',
        qs: this.agencyQs()
      });
      return response;
    });

    _defineProperty(this, "startPatientChat", async (request, correlationId) => {
      const data = {
        patientNumber: request.patientNumber
      };

      if (request.responseDetail) {
        data.responseDetail = request.responseDetail;
      }

      if (request.fromDate) {
        data.fromDate = request.fromDate;
      }

      if (request.toDate) {
        data.toDate = request.toDate;
      }

      const headers = {};

      if (correlationId) {
        headers['X-Correlation-ID'] = correlationId;
      }

      const response = await this.post({
        api: '/api/agents/patient-chat/start',
        qs: this.agencyQs(),
        data,
        headers
      });
      return response;
    });

    _defineProperty(this, "sendPatientChatMessage", async (request, correlationId) => {
      const data = {
        conversationId: request.conversationId,
        message: request.message
      };

      if (request.responseDetail) {
        data.responseDetail = request.responseDetail;
      }

      const headers = {};

      if (correlationId) {
        headers['X-Correlation-ID'] = correlationId;
      }

      const response = await this.post({
        api: '/api/agents/patient-chat/message',
        qs: this.agencyQs(),
        data,
        headers
      });
      return response;
    });

    _defineProperty(this, "cancelPatientChatMessage", async conversationId => {
      const response = await this.post({
        api: '/api/agents/patient-chat/cancel',
        qs: this.agencyQs(),
        data: {
          conversationId
        }
      });
      return response;
    });

    _defineProperty(this, "listPatientChatConversations", async (params = {}) => {
      var _params$page2, _params$size2;

      const qs = _objectSpread({
        page: String((_params$page2 = params.page) !== null && _params$page2 !== void 0 ? _params$page2 : 0),
        size: String((_params$size2 = params.size) !== null && _params$size2 !== void 0 ? _params$size2 : 20),
        sort: params.sort || 'lastMessageAt,DESC'
      }, this.agencyQs());

      const response = await this.get({
        api: '/api/agents/patient-chat/conversations',
        qs
      });
      return response;
    });

    _defineProperty(this, "listPatientConversationsForPatient", async (patientNumber, params = {}) => {
      var _params$page3, _params$size3;

      const qs = _objectSpread(_objectSpread({
        page: String((_params$page3 = params.page) !== null && _params$page3 !== void 0 ? _params$page3 : 0),
        size: String((_params$size3 = params.size) !== null && _params$size3 !== void 0 ? _params$size3 : 50),
        sort: params.sort || 'startedAt,DESC'
      }, params.range), this.agencyQs());

      const response = await this.get({
        api: `/api/agents/patient-chat/patients/${encodeURIComponent(patientNumber)}/conversations`,
        qs
      });
      return response;
    });

    _defineProperty(this, "listPatientChatTurnsForPatient", async (patientNumber, params = {}) => {
      var _params$page4, _params$size4;

      const qs = _objectSpread({
        page: String((_params$page4 = params.page) !== null && _params$page4 !== void 0 ? _params$page4 : 0),
        size: String((_params$size4 = params.size) !== null && _params$size4 !== void 0 ? _params$size4 : 50)
      }, this.agencyQs());

      const response = await this.get({
        api: `/api/agents/patient-chat/patients/${encodeURIComponent(patientNumber)}/turns`,
        qs
      });
      return response;
    });

    _defineProperty(this, "listPatientChatPatients", async (siteId, range) => {
      const response = await this.get({
        api: '/api/agents/patient-chat/patients',
        qs: _objectSpread(_objectSpread({
          siteId: String(siteId)
        }, range), this.agencyQs())
      });
      return response;
    });

    _defineProperty(this, "getPatientChatTurn", async id => {
      const response = await this.get({
        api: `/api/agents/patient-chat/turns/${id}`,
        qs: this.agencyQs()
      });
      return response;
    });

    _defineProperty(this, "endPatientChatConversation", async id => {
      const response = await this.post({
        api: `/api/agents/patient-chat/conversations/${id}/end`,
        qs: this.agencyQs(),
        data: {}
      });
      return response;
    });

    _defineProperty(this, "ratePatientChatAnswer", async request => {
      const {
        requestId,
        rating,
        comment = null
      } = request;
      const response = await this.post({
        api: '/api/agents/patient-chat/feedback',
        method: 'PUT',
        qs: this.agencyQs(),
        data: {
          requestId,
          rating,
          comment
        }
      });
      return response;
    });

    _defineProperty(this, "getPatientChatUsage", async patientNumber => {
      const qs = _objectSpread({}, this.agencyQs());

      if (patientNumber) {
        qs.patientNumber = patientNumber;
      }

      const response = await this.get({
        api: '/api/agents/patient-chat/usage',
        qs
      });
      return response;
    });

    _defineProperty(this, "getPatientChatConversation", async id => {
      const response = await this.get({
        api: `/api/agents/patient-chat/conversations/${id}`,
        qs: this.agencyQs()
      });
      return response;
    });

    _defineProperty(this, "listPatientChatMessages", async (id, params = {}) => {
      var _params$page5, _params$size5;

      const qs = _objectSpread({
        page: String((_params$page5 = params.page) !== null && _params$page5 !== void 0 ? _params$page5 : 0),
        size: String((_params$size5 = params.size) !== null && _params$size5 !== void 0 ? _params$size5 : 50),
        sort: params.sort || 'createdAt,ASC'
      }, this.agencyQs());

      const response = await this.get({
        api: `/api/agents/patient-chat/conversations/${id}/messages`,
        qs
      });
      return response;
    });

    _defineProperty(this, "listPatientChatUnanswered", async id => {
      const response = await this.get({
        api: `/api/agents/patient-chat/conversations/${id}/unanswered`,
        qs: this.agencyQs()
      });
      return response;
    });

    this.url = new URL(url);
    this.keycloak = keycloak;
    this.agency = resolveAgency(agency, keycloak);
    Object.defineProperty(this.commonHeaders, 'Authorization', {
      get() {
        const {
          token
        } = keycloak;
        return `Bearer ${token}`;
      },

      set(_newValue) {// need a setter
      }

    });
    this.commonHeaders.Authorization = `Bearer ${keycloak.token}`;
  }

}
export default AgentGateway;